'use client';

import React, { useEffect, useRef, useState, type ReactNode } from 'react';
import { CloseIcon } from '@/assets';
import { useIsModalOpen, useModalStore } from '@/store';
import type { ModalId } from '@/utils/modal-registry';
import Portal from './Portal';

type ModalHostProps = {
  /** Which modal this host renders. It is open only when the store says so. */
  modalId: ModalId;
  /** The modal's content — rendered only while the modal is mounted. */
  children: ReactNode;
};

/**
 * Renders one same-page modal, and owns its mount/enter/exit lifecycle.
 *
 * Lifted verbatim out of `GlobalNavBar` and `GlobalNavBarRedesign`, where this
 * exact block was duplicated byte for byte. Both still render it, so the navbar
 * remains the component that listens to the store and puts the modal on screen —
 * only the duplication is gone.
 *
 * Adding a second modal is now a `MODAL_IDS` entry plus one more `<ModalHost>`;
 * nothing about the state flow or the link adapter has to change.
 */
export const ModalHost = ({ modalId, children }: ModalHostProps) => {
  const isOpen = useIsModalOpen(modalId);
  const closeModal = useModalStore((state) => state.closeModal);

  // `renderModal` keeps the modal mounted through its fade-out. It turns true
  // synchronously during render when the store opens (React allows setState
  // during render to react to a changed external value; it is applied before
  // commit with no extra paint and is not a setState-in-effect), and turns
  // false from a timeout callback after the close animation completes.
  const [renderModal, setRenderModal] = useState(false);
  const [hasAnimatedIn, setHasAnimatedIn] = useState(false);
  // Tracks the previous `isOpen` so we can detect the open edge during render.
  const [prevIsOpen, setPrevIsOpen] = useState(isOpen);
  const closeTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const enterTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  if (isOpen !== prevIsOpen) {
    setPrevIsOpen(isOpen);
    if (isOpen) {
      // Modal just opened: mount it and reset the enter-animation flag.
      setRenderModal(true);
      setHasAnimatedIn(false);
    }
  }

  // The fade-out is purely derived: it's active whenever the modal is still
  // mounted but the store has closed.
  const isClosing = renderModal && !isOpen;

  // Drive the enter animation: a short delay after mount flips `hasAnimatedIn`.
  // setState here runs in a timeout callback, so it is not a setState-in-effect.
  useEffect(() => {
    if (renderModal && isOpen) {
      if (enterTimeoutRef.current) {
        clearTimeout(enterTimeoutRef.current);
      }
      enterTimeoutRef.current = setTimeout(() => {
        setHasAnimatedIn(true);
        enterTimeoutRef.current = null;
      }, 30);
    }

    return () => {
      if (enterTimeoutRef.current) {
        clearTimeout(enterTimeoutRef.current);
        enterTimeoutRef.current = null;
      }
    };
  }, [renderModal, isOpen]);

  // Once closing, schedule the unmount after the fade completes. The unmount
  // setState runs in a timeout callback, so it is not a setState-in-effect.
  useEffect(() => {
    if (!isClosing) {
      return;
    }

    if (closeTimeoutRef.current) {
      clearTimeout(closeTimeoutRef.current);
    }
    closeTimeoutRef.current = setTimeout(() => {
      setRenderModal(false);
      setHasAnimatedIn(false);
      closeTimeoutRef.current = null;
    }, 250);

    return () => {
      if (closeTimeoutRef.current) {
        clearTimeout(closeTimeoutRef.current);
        closeTimeoutRef.current = null;
      }
    };
  }, [isClosing]);

  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'auto';
    }
    return () => {
      document.body.style.overflow = 'auto';
    };
  }, [isOpen]);

  // If the host unmounts while the store still thinks the modal is open — which
  // happens when a CTA inside the modal navigates to a route with a different
  // layout (e.g. `/get-started/[uid]` uses `LogosLayout`, no navbar, no host) —
  // the store keeps the stale `activeModal`. Next time this layout remounts,
  // the open-edge never fires (prevIsOpen initializes to true), so the modal
  // never re-renders and body scroll stays locked; and the Get Started button
  // becomes a no-op because `openModal` bails when `activeModal` already matches.
  // Closing on unmount resets that so navigating back leaves a clean slot.
  useEffect(
    () => () => {
      if (useModalStore.getState().activeModal === modalId) {
        useModalStore.getState().closeModal();
      }
    },
    [modalId],
  );

  if (!renderModal) {
    return null;
  }

  return (
    <Portal>
      <div
        className={`fixed inset-0 z-[9999] bg-black/80 backdrop-blur-xl pointer-events-auto transition-opacity duration-500 ease-out ${
          isClosing ? 'opacity-0' : hasAnimatedIn ? 'opacity-100' : 'opacity-0'
        }`}
      >
        <div
          className={`relative h-full overflow-y-auto overscroll-contain transition-all duration-500 ease-out will-change-transform ${
            isClosing
              ? 'opacity-0 -translate-y-10'
              : hasAnimatedIn
                ? 'opacity-100 translate-y-0'
                : 'opacity-0 -translate-y-10'
          }`}
        >
          <button
            type="button"
            className="absolute top-8 right-8 z-[10000] flex h-8 w-8 items-center justify-center rounded-[8px] bg-[#05f273] text-[#041118] transition-opacity hover:opacity-90 focus:outline-none focus-visible:ring-2 focus-visible:ring-[#05f273]/60"
            aria-label="Close popup"
            onClick={closeModal}
            tabIndex={isClosing || !hasAnimatedIn ? -1 : 0}
          >
            <CloseIcon width={24} height={24} fill="#000000" />
          </button>
          <div className="min-h-full">{children}</div>
        </div>
      </div>
    </Portal>
  );
};
